'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

// `.br-sign-in.inverted` do DS (o "fundo escuro" do examples.html do core):
// o DS não inverte propriedades, troca os tokens. Mesma tática do `item`
// (inkDarkTokens) e do `divider` (prop `inverted`) — nunca classes `dark:`.
const invertedTokens = [
  '[--color:var(--color-dark)] [--color-rgb:var(--color-dark-rgb)]',
  '[--interactive:var(--interactive-dark)]',
  '[--interactive-rgb:var(--interactive-dark-rgb)]',
  '[--visited:var(--visited-dark)]',
  '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
  '[--focus-color:var(--focus-color-dark)]',
].join(' ')

type SignInProps = Omit<React.ComponentProps<typeof Button>, 'variant'> & {
  /**
   * Ênfase do DS. `default` é o `.br-sign-in` sem modificador (a "Ênfase
   * Secundária" do examples.html; `version=standart` do UIKIT): superfície
   * clara com tinta `--interactive`. `primary` é o `.br-sign-in.primary`
   * (`version=alternative` do UIKIT): fundo `--interactive-light` com tinta
   * `--color-dark`.
   */
  variant?: 'default' | 'primary'
  /**
   * `.br-sign-in.inverted` do DS: versão para fundo escuro/colorido. Remapeia
   * os tokens de tinta, hover, pressed e foco para os pares `*-dark`.
   */
  inverted?: boolean
}

function SignIn({
  className,
  variant = 'default',
  inverted = false,
  circle,
  asChild = false,
  ...props
}: SignInProps) {
  return (
    <Button
      // `tertiary` é a base transparente do Button; a superfície do sign-in
      // entra logo abaixo, servida em --sign-in-background.
      variant="tertiary"
      circle={circle}
      asChild={asChild}
      // O markup do DS marca o sign-in como `type="button"`: ele aciona o login,
      // não envia o formulário em que estiver.
      {...(asChild ? {} : { type: 'button' as const })}
      className={cn(
        // Tokens que a raiz **serve** (o DS os declara no próprio componente):
        // a superfície e a altura máxima da marca em <img> (20px no core).
        '[--sign-in-img:var(--spacing-scale-2xh)]',
        // Superfície: o core usa `--gray-2` para os dois tipos; o UIKIT desenha
        // a pílula em gray-01 e o ícone circular em gray-02 (ver CHANGELOG).
        variant === 'default' && [
          '[--sign-in-background:var(--gray-2)]',
          'bg-[var(--sign-in-background)] text-[var(--interactive)]',
        ],
        variant === 'primary' && [
          '[--sign-in-background:var(--interactive-light)]',
          'bg-[var(--sign-in-background)] text-[var(--color-dark)]',
          // Sobre azul cheio a sobreposição é clara e nas opacidades "dark".
          '[--interactive-rgb:var(--color-dark-rgb)]',
          '[--hover:var(--hover-dark)] [--pressed:var(--pressed-dark)]',
        ],
        inverted && [
          invertedTokens,
          variant === 'default' && [
            '[--sign-in-background:var(--background-dark)]',
            'bg-[var(--sign-in-background)] text-[var(--interactive-dark)]',
          ],
          variant === 'primary' && [
            '[--sign-in-background:var(--interactive-dark)]',
            'bg-[var(--sign-in-background)] text-[var(--background-dark)]',
            '[--interactive-rgb:var(--background-dark-rgb)]',
          ],
        ],
        // O sign-in aperta o padding do botão de 3x para 2x — exceto no tipo
        // icônico (`circle`), que o DS zera.
        !circle && 'px-[var(--spacing-scale-2x)]',
        // O UIKIT separa ícone/marca do rótulo por 4px (o core deixa a cargo do
        // `&nbsp;` do markup).
        'gap-[var(--spacing-scale-half)]',
        // Marca do gov.br em imagem (tipo externo do UIKIT).
        '[&_img]:max-h-[var(--sign-in-img)] [&_img]:w-auto',
        className
      )}
      data-slot="sign-in"
      {...props}
    />
  )
}

export { SignIn }
